import type {
  ExampleParagraph,
  ParagraphContext,
  RuleSeverity,
  StyleProfile,
  StyleRule,
  WritingStyle
} from '@/types/writingStyle';
import { writingStyleHelper } from '@/utils/writingStyleHelpers';

const EXPORT_VERSION = '1.0.0';

const isParagraphContext = (value: unknown): value is ParagraphContext => {
  return (
    value === 'action' ||
    value === 'description' ||
    value === 'dialogue' ||
    value === 'exposition' ||
    value === 'general' ||
    value === 'introspection'
  );
};

const isRuleSeverity = (value: unknown): value is RuleSeverity => {
  return value === 'hard-rule' || value === 'strong-guideline' || value === 'soft-guideline';
};

const sanitizeString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const cloneParagraph = (styleId: string, input: Partial<ExampleParagraph>): ExampleParagraph => {
  const seed = writingStyleHelper.createEmptyParagraph(styleId);
  const context = isParagraphContext(input.context) ? input.context : 'general';
  const highlighted =
    Array.isArray(input.highlightedTechniques) && input.highlightedTechniques.length > 0
      ? input.highlightedTechniques
          .map((item) => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean)
      : undefined;

  return {
    ...seed,
    label: sanitizeString(input.label) ?? seed.label,
    text: sanitizeString(input.text) ?? seed.text,
    context,
    notes: sanitizeString(input.notes),
    highlightedTechniques: highlighted,
    uiState: {
      isExpanded: Boolean(input.uiState?.isExpanded)
    },
    createdAt: typeof input.createdAt === 'string' ? input.createdAt : seed.createdAt,
    updatedAt: typeof input.updatedAt === 'string' ? input.updatedAt : seed.updatedAt
  };
};

const cloneRule = (styleId: string, input: Partial<StyleRule>): StyleRule => {
  const seed = writingStyleHelper.createEmptyRule(styleId);
  const severity = isRuleSeverity(input.severity) ? input.severity : 'strong-guideline';
  return {
    ...seed,
    rule: sanitizeString(input.rule) ?? seed.rule,
    rationale: sanitizeString(input.rationale) ?? seed.rationale,
    severity,
    goodExample: sanitizeString(input.goodExample) ?? seed.goodExample,
    badExample: sanitizeString(input.badExample) ?? seed.badExample,
    notes: sanitizeString(input.notes) ?? '',
    uiState: {
      isExpanded: Boolean(input.uiState?.isExpanded)
    },
    createdAt: typeof input.createdAt === 'string' ? input.createdAt : seed.createdAt,
    updatedAt: typeof input.updatedAt === 'string' ? input.updatedAt : seed.updatedAt
  };
};

export interface WritingStyleExportPayload {
  version: string;
  exportedAt: string;
  writingStyle: WritingStyle;
}

export const serializeWritingStyle = (style: WritingStyle): string => {
  const payload: WritingStyleExportPayload = {
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    writingStyle: {
      ...style,
      exampleParagraphs: style.exampleParagraphs.map((paragraph) => ({ ...paragraph })),
      styleRules: style.styleRules.map((rule) => ({ ...rule }))
    }
  };
  return JSON.stringify(payload, null, 2);
};

export const deserializeWritingStyle = (manuscriptId: string, json: string): WritingStyle => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (error) {
    throw new Error('Unable to parse writing style file.');
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Invalid writing style payload.');
  }

  const container = parsed as Partial<WritingStyleExportPayload> & { data?: unknown };
  const source = (container.writingStyle ?? (container.data as WritingStyle | undefined)) ?? (parsed as WritingStyle);

  if (!source || typeof source !== 'object') {
    throw new Error('Writing style data is missing.');
  }

  const base = writingStyleHelper.createEmptyWritingStyle(manuscriptId);
  const styleId = base.id;
  const input = source as Partial<WritingStyle>;

  const profileInput = (input.styleProfile ?? {}) as Partial<StyleProfile>;

  const exampleParagraphs = Array.isArray(input.exampleParagraphs)
    ? input.exampleParagraphs.map((entry) => cloneParagraph(styleId, entry))
    : [];

  const styleRules = Array.isArray(input.styleRules)
    ? input.styleRules.map((entry) => cloneRule(styleId, entry))
    : [];

  return {
    ...base,
    styleProfile: {
      ...base.styleProfile,
      baseTemplate: profileInput.baseTemplate ?? base.styleProfile.baseTemplate,
      essence: sanitizeString(profileInput.essence) ?? base.styleProfile.essence,
      name: sanitizeString(profileInput.name) ?? base.styleProfile.name,
      pov: profileInput.pov ?? base.styleProfile.pov,
      tense: profileInput.tense ?? base.styleProfile.tense,
      vocabulary: profileInput.vocabulary,
      tone: profileInput.tone,
      sentenceLength: profileInput.sentenceLength,
      narrativeDistance: profileInput.narrativeDistance
    },
    exampleParagraphs,
    styleRules,
    notes: sanitizeString(input.notes) ?? '',
    createdAt: typeof input.createdAt === 'string' ? input.createdAt : base.createdAt,
    updatedAt: new Date().toISOString()
  };
};

